import React, { useEffect, useMemo, useState } from 'react';

import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { styled } from '@mui/material/styles';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import WarningIcon from '@mui/icons-material/Warning';

import RestrictComponent from '../../../../shared/RestrictComponent';
import Autocomplete from '../../../../shared/Autocomplete';
import Input from '../../../../shared/Input';

import { sortAlphabetically } from '../../../../../util/utils';

import { userRoles, caseConstants } from '../../../../../constants';

import pdfIcon from '../../../../../assets/preop_icon.svg';
import videoIcon from '../../../../../assets/block_icon.svg';
import imageIcon from '../../../../../assets/image_icon.svg';
import Dropzone from '../../../../shared/dropzone/Dropzone';
import Alert from '../../../../shared/Alert';

import { selectedProcedureFlows } from '../../../../../constants/cases';

const PrimaryLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
}));

const { GLOBAL_ADMIN, SALES_REP, PRACTICE_MANAGER, SURGEON, COUNTRY_ADMIN, REGISTRAR, ENGINEER, DISTRIBUTOR_ADMIN } = userRoles;
const { procedureFlows, cmfExtendedProcedureFlows, matorthoProcedureFlows } = caseConstants;

const planningFileFlows = [
  selectedProcedureFlows.HTO,
  selectedProcedureFlows.DFO,
  selectedProcedureFlows.DOUBLE_LEVEL_OSTEOTOMY,
  selectedProcedureFlows.ACL,
  selectedProcedureFlows.CMF_EXTENDED,
  selectedProcedureFlows.MATORTHO,
  selectedProcedureFlows.MATORTHO_KNEE,
  selectedProcedureFlows.MATORTHO_HIP,
  selectedProcedureFlows.VILEX,
  selectedProcedureFlows.VILEX_H2,
  selectedProcedureFlows.VILEX_H3,
];

const getColor = (procedureFlow) => {
  if (procedureFlow === procedureFlows.ONCOL) {
    return "#7266FA";
  }
  if (procedureFlow === procedureFlows.CUSTOM_EXTENDED) {
    return 'var(--color-main)';
  }
  if (matorthoProcedureFlows.includes(procedureFlow)) {
    return "#AA2415";
  }
  if (procedureFlow === procedureFlows.DOUBLE_LEVEL_OSTEOTOMY) {
    return "#ee9321";
  }
  if (procedureFlow === procedureFlows.VILEX) {
    return "#BD1435";
  }
  return "#33CC99";
};

export const Step0CaseInfo = ({
  distributors,
  distributor,
  setDistributor,
  selectedFlow,
  selectedParentFlow,
  surgeons,
  setSurgeon,
  procedures,
  hospitals,
  currentSurgeon,
  surgeon,
  surgeonList,
  hospital,
  setHospital,
  documentList,
  procedure,
  setProcedure,
  date,
  setDate,
  isAdmin,
  psi,
  startDate,
  setPsi,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  gender,
  setGender,
  dateOfBirth,
  setDateOfBirth,
  procedureSide,
  setProcedureSide,
  checked,
  setChecked,
  reports = [],
  caseNote,
  setCaseNote,
  onUpload,
  onRemove,
  dateTBC,
  setDateTBC,
  leadDays,
  planningFileRequired,
  setPlanningFileRequired,
  consultationDate,
  setConsultationDate
}) => {
  const [filteredHospitals, setHospitals] = useState(currentSurgeon ? hospitals : []);
  const [showAlert, toggleAlert] = useState(false);

  const handleDistributorChange = (value) => {
    setDistributor(value);

    if (!currentSurgeon) {
      setSurgeon(null);
      setHospitals([]);
    }

    setHospital(null);
    setProcedure(null);
  };

  const getIcon = (name) => {
    if (name && name.includes('.pdf')) {
      return pdfIcon;
    }
    if (name && name.includes('.mp4')) {
      return videoIcon;
    }
    return imageIcon;
  };

  const filteredSurgeons = useMemo(
    () =>
      surgeonList
        .filter((item) => item.distributor === distributor?.id)
        .map((surgeon) => surgeons.find((s) => s.uid === surgeon.userId))
        .filter((surgeon) => !!surgeon),
    [surgeonList, surgeons, distributor]
  );

  const getFilteredHospitals = (user) => {
    const currentSurgeon = user
      ? surgeonList
          .filter((item) => item.userId === user.uid)
          .find((surgeon) => surgeon.distributor === distributor?.id)
      : null;

    if (!currentSurgeon) {
      return hospitals;
    }

    return hospitals.filter((item) => currentSurgeon.hospitals.includes(item.id));
  };

  const handleSurgeonChange = (user) => {
    const filteredHospitals = getFilteredHospitals(user);

    setSurgeon(user);
    setHospitals(filteredHospitals);
    setHospital(filteredHospitals.length ? filteredHospitals[0] : null);
  };

  const filteredProcedures = useMemo(
    () =>
      procedures.filter((item) =>
        distributor?.assignedProcedures?.find((assign) => assign?.id === item?.id) && (
          // item?.flow === selectedFlow || item?.flow?.includes(selectedFlow) || item?.flow === selectedParentFlow || item?.flow?.includes(selectedParentFlow)
          item?.flow === selectedFlow || item?.flow?.includes(selectedFlow)
        )
      ),
    [procedures, distributor, selectedFlow]
  );

  useEffect(() => {
    const newSurgeon = surgeonList.filter((s) => s.distributor === distributor?.id);
    const findSurgeon = newSurgeon.find((s) => s.userId === surgeon?.uid);
    const user = surgeons.find((s) => s.uid === findSurgeon?.userId);

    const filteredHospitals = getFilteredHospitals(user);

    setHospitals(filteredHospitals);
  }, [surgeon, distributor, hospital]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="case-step-row">
        <div className="column">
          <div className="modal-item-circle" />
        </div>
        <div className="modal-item-content">
          <div>New Case</div>
          <div className="modal-item-block">
            <div className="block-header">Case info</div>

            <Grid container spacing={2}>
              <RestrictComponent roles={[GLOBAL_ADMIN, SURGEON, SALES_REP, PRACTICE_MANAGER, COUNTRY_ADMIN, REGISTRAR, ENGINEER, DISTRIBUTOR_ADMIN]}>
                {!!distributors?.length && (
                  <Grid size={{ xs: 12, md: 12 }}>
                    <div className="field-container">
                      <div className="field-title">Distributor</div>
                      <div className="field-content">
                        <Autocomplete
                          value={distributor}
                          onChange={handleDistributorChange}
                          options={distributors.sort(sortAlphabetically('name'))}
                          getOptionLabel={(option) => option.name}
                        />
                      </div>
                    </div>
                  </Grid>
                )}
              </RestrictComponent>

              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Assigned surgeon</div>
                  <div className="field-content">
                    {currentSurgeon ? (
                      <div className="step0-surgeon-name">
                        Dr. {currentSurgeon.firstName} {currentSurgeon.lastName}
                      </div>
                    ) : (
                      <Autocomplete
                        value={surgeon}
                        onChange={handleSurgeonChange}
                        options={filteredSurgeons}
                        getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}`}
                      />
                    )}
                  </div>
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Hospital</div>
                  <div className="field-content">
                    <Autocomplete
                      value={hospital}
                      onChange={setHospital}
                      options={filteredHospitals.sort(sortAlphabetically('name'))}
                      getOptionLabel={(option) => option.name}
                    />
                  </div>
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Procedure</div>
                  <div className="field-content">
                    <Autocomplete
                      value={procedure}
                      onChange={setProcedure}
                      options={filteredProcedures.sort(sortAlphabetically('name'))}
                      getOptionLabel={(option) => option.name}
                    />
                  </div>
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container" >
                  <div className="field-title">Targeted surgery date</div>
                  <div className="field-content">
                    <DatePicker
                      desktopModeMediaQuery="(min-width: 0px)"
                      format="DD/MM/YYYY"
                      value={date}
                      onChange={(newDate) => setDate(newDate)}
                      disabled={dateTBC || !procedure}
                      minDate={
                        isAdmin || psi === 'no' || dateTBC
                          ? undefined
                          : startDate
                      }
                      onOpen={() => {
                        if (!procedure) {
                          toggleAlert(true);
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'standard',
                          onClick: () => {
                            if (!procedure) {
                              toggleAlert(true);
                            }
                          },
                        },
                      }}
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={<Checkbox color="primary" checked={dateTBC} onChange={(e) => setDateTBC(e.target.checked)} />}
                      label="TBC (if date not yet confirmed)"
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
          {(
            matorthoProcedureFlows.includes(selectedFlow) ||
            ![procedureFlows.ACL, procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow)
          ) && (
            <div className="modal-item-block">
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <div className="field-container">
                    <div className="field-title">PSI required for this case</div>
                    <div className="field-content">
                      <div className="radio-group-container">
                        <RadioGroup aria-label="psi" name="psi" value={psi} onChange={(e) => setPsi(e.target.value)}>
                          <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio color="primary" />} label="No (skip)" />
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          )}
          <div className="modal-item-block">
            <div className="block-header">Patient info</div>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Patient name (name, surname)</div>
                  <div className="field-content d-flex">
                    <div className="flex-1">
                      <Input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First Name"
                      />
                    </div>
                    <div className="m-l-lg flex-1">
                      <Input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last Name"
                      />
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Gender</div>
                  <div className="field-content">
                    <div className="radio-group-container">
                      <RadioGroup
                        aria-label="gender"
                        name="gender"
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <FormControlLabel value="male" control={<Radio color="primary" />} label="Male" />
                        <FormControlLabel value="female" control={<Radio color="primary" />} label="Female" />
                      </RadioGroup>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="field-container">
                  <div className="field-title">Date of birth</div>
                  <div className="field-content">
                    <DatePicker
                      desktopModeMediaQuery="(min-width: 0px)"
                      format="DD/MM/YYYY"
                      value={dateOfBirth}
                      onChange={(newDate) => setDateOfBirth(newDate)}
                      disableFuture
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'standard',
                        },
                      }}
                    />
                  </div>
                </div>
              </Grid>
              {selectedFlow !== procedureFlows.CMF && selectedFlow !== procedureFlows.CMF_EXTENDED && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className="field-container">
                    <div className="field-title">Procedure side</div>
                    <div className="field-content">
                      <div className="radio-group-container">
                        <RadioGroup
                          aria-label="procedureSide"
                          name="procedureSide"
                          value={procedureSide}
                          onChange={(e) => setProcedureSide(e.target.value)}
                        >
                          <FormControlLabel value="left" control={<Radio color="primary" />} label="Left" />
                          <FormControlLabel value="right" control={<Radio color="primary" />} label="Right" />
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                </Grid>
              )}
            </Grid>

            {[procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow) && (
              <div className="m-t-lg d-flex align-center">
                <WarningIcon style={{ color: getColor(selectedFlow) }}/>
                <div className="m-l-sm" style={{ color: getColor(selectedFlow) }}>Custom case note or consultation report required to proceed</div>
              </div>
            )}

            <div className="">
              <div className="modal-item-block">
                <div className="block-header">Case note</div>
                <Input
                  multiline
                  id="caseNote"
                  type="text"
                  placeholder="Type any case related notes here"
                  value={caseNote}
                  onChange={(e) => setCaseNote(e.target.value)}
                  minRows={3}
                />
              </div>
            </div>

            {planningFileFlows.includes(selectedFlow) && (
              <div className="m-t-lg">
                <div className="block-header">Planning File</div>
                <div className='secondary'>Do you require a planning file to present to the patient prior to surgery?</div>

                <Grid size={{ xs: 12, md: 6 }}>
                  <div className="radio-group-container">
                    <RadioGroup
                      aria-label="planningFileRequired"
                      name="planningFileRequired"
                      value={planningFileRequired}
                      onChange={(e) => setPlanningFileRequired(e.target.value)}
                    >
                      <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                      <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                    </RadioGroup>
                  </div>
                </Grid>
                {planningFileRequired === 'yes' && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DatePicker
                      desktopModeMediaQuery="(min-width: 0px)"
                      format="DD/MM/YYYY"
                      value={consultationDate}
                      onChange={(newDate) => setConsultationDate(newDate)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'standard',
                        },
                      }}
                    />
                  </Grid>
                )}
              </div>
            )}

            <div>
              <div className="modal-item-block">
                <div className="block-header">
                  {`Surgeon consultation report${selectedFlow === procedureFlows.CMF ? ' (recommended)' : ''}`}
                </div>
                <Dropzone
                  files={reports}
                  onUpload={onUpload}
                  icon="scan"
                  onRemove={onRemove}
                />
              </div>
            </div>

            {!!documentList.length && (
              <div>
                <div style={{ margin: '16px 0' }}>
                  <div className="secondary uppercase font-size-xs m-b-md">Documents</div>
                  <div className="document-list">
                    {documentList.map((document) => (
                      <div key={document.id} className="m-b-sm">
                        <img src={getIcon(document.name)} alt="file-icon" style={{ marginRight: '8px', width: '20px' }} />
                        <PrimaryLink href={document.url} target="_blank" rel="noopener noreferrer">
                          {document.name}
                        </PrimaryLink>
                      </div>
                    ))}
                  </div>
                </div>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
                  label="I have reviewed all relevant procedure documentation"
                />
              </div>
            )}
          </div>
        </div>

        <Alert
          variant="warning"
          message="Please select procedure before selecting surgery date"
          open={showAlert}
          onClose={() => toggleAlert(false)}
        />
      </div>
    </LocalizationProvider>
  );
};
